import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../api/axios";
import TableCell from "../../common/ui/TableCell";
import SignTableDocByRole from "../../approval/SignTable_doc_role";
import PeopleCellLoader from "../../common/people/PeopleCellLoader";
import SignTableDocSingle from "../../approval/SignTable_doc_single";
import { updateQualityMeta, getQualityMeta, getQualityMetaList } from "../../../api/quality/meta";

export default function QualityMIView({ data, project }) {
  const navigate = useNavigate();

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const [showAgentSign, setShowAgentSign] = useState(false);
  const [saving, setSaving] = useState(false);



  const meta = data?.meta || {};
  const o = meta.officialMeta || {};
  const dns = data?.doc_dns || data?.doc?.dns;

  const isSiteAgent = Number(currentUser?.id) === Number(o.site_agent?.id);

  /* ===============================
     승인 처리 (정답)
     - quality/meta PATCH
  =============================== */
  const handleApprove = async () => {
    if (!dns) return;

    try {
      setSaving(true);

      await updateQualityMeta(dns, {
        ...meta,
        officialMeta: {
          ...o,
          site_agent_approved: true,
          site_agent_approved_at: new Date().toISOString(),
          site_agent_approved_id: currentUser.id,
          site_agent_approved_name: currentUser.name,
        },
      });

      setShowAgentSign(true);
    } catch (e) {
      console.error(e);
      alert("승인 저장 중 오류가 발생했습니다.");
    } finally {
      setSaving(false);
    }
  };

  /* ===============================
     체크리스트 작성 이동
  =============================== */
  const goCreateChecklist = () => {
    if (!dns || !project) return;

    navigate(`/project/${project}/quality/checklist/new?mi_dns=${dns}`);
  };

  const approved = o.site_agent_approved === true || showAgentSign;

  /* =====================================================
     [추가] PC(체크리스트) 승인 여부 + 통보서 로직
  ===================================================== */
  const [ckApproved, setCkApproved] = useState(false);
  const [ckDns, setCkDns] = useState(null);
  const [savingNotice, setSavingNotice] = useState(false);

  // 통보 데이터
  const [requestPeople, setRequestPeople] = useState([]);
  const [pmManager, setPmManager] = useState([]); // 🔥 [추가] PM 단장 값

  const [notice, setNotice] = useState({
    inspect_date: "",
    result: "",
    instruction: "",
    notice_date: "",
  });

  // 1. 체크리스트(PC) 조회
  // 🔥 [수정] 404 에러 해결: api/quality/docs/ -> api/quality/meta/ (using getQualityMetaList)
  useEffect(() => {
    if (!dns || !project) return;
    (async () => {
      try {
        // QualityMeta List 조회
        const res = await getQualityMetaList({
          project,
          doc_type: "PC", // or "PC" for form_type filter if backend supports it
          mi_dns: dns,
          page_size: 20, // 🔥 [수정] 1 -> 20. MI(자신)와 PC가 같이 올 수 있으므로 충분히 조회
          ordering: "-created_at",
        });

        // getQualityMetaList returns data directly (array or pagination object)
        const results = Array.isArray(res) ? res : res.results || [];

        console.log("🔍 [DEBUG] Finding Linked Checklist (PC)...");
        console.log(`- Base Criteria: mi_dns == '${dns}'`);
        console.log(`- Candidates Found: ${results.length}`);

        // 🔥 [Self-Filter] Exclude self (MI) and find PC
        const pc = results.find((doc, index) => {
          const docDns = doc.dns || doc.doc_dns;
          const docType = doc.meta?.doc_type || doc.form_type;

          const isSelf = docDns === dns;
          const isPC = docType === "PC";

          console.log(`  [${index}] DNS: ${docDns}, Type: ${docType}`);
          if (isSelf) console.log(`     -> Skipped (Self/MI)`);
          else if (!isPC) console.log(`     -> Skipped (Not PC)`);
          else console.log(`     -> ✅ MATCH!`);

          return !isSelf && isPC;
        });

        console.log("🔍 [DEBUG] Selected PC Document:", pc);

        if (pc) {
          setCkDns(pc.dns || pc.doc_dns);
          const header = pc.meta?.header || {};
          const isSigned = !!(header.is_signed || header.approval_date);
          console.log("🔍 [DEBUG] Approval Status Check:");
          console.log(`- is_signed: ${header.is_signed}`);
          console.log(`- approval_date: ${header.approval_date}`);
          console.log(`- FINAL RESULT (ckApproved): ${isSigned}`);

          setCkApproved(isSigned);
        } else {
          console.log("❌ [DEBUG] No valid Checklist found. Notification form will NOT be shown.");
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, [dns, project]);

  // 2. 기존 통보 데이터 로로드
  const savedNotice = meta.notice;
  const savedNoticeExists = !!savedNotice;

  useEffect(() => {
    if (savedNotice) {
      setNotice({
        inspect_date: savedNotice.inspect_date || "",
        result: savedNotice.result || "",
        instruction: savedNotice.instruction || "",
        notice_date: savedNotice.notice_date || "",
      });
    } else {
      // 저장된게 없으면 초기값 설정

      // 1. 수신자(requestPeople) <- 현장대리인 자동 세팅
      const contractor = data?.meta?.header?.assigned_people_info?.constructor || data?.header?.assigned_people_info?.constructor;
      if (contractor && contractor.id) {
        setRequestPeople([contractor]);
      }

      setNotice((prev) => ({
        ...prev,
        notice_date: new Date().toISOString().split("T")[0],
      }));
    }
  }, [savedNotice, data, currentUser]);

  /* =====================================================
     통보 저장 핸들러
  ===================================================== */
  const saveNotice = async () => {
    // 1. 필수값 체크 (결과, 통보일자)
    // 🔥 [수정] inspect_date(검수일시)는 UI에 없고 시스템값 사용이므로 Validation 제거
    if (!notice.result || !notice.notice_date) {
      alert("검측결과와 통보일자를 입력하세요.");
      return;
    }
    if (!requestPeople?.length) {
      alert("수신자를 선택하세요.");
      return;
    }

    // PM 단장 선택 필수인지 체크 (선택사항이면 제거)
    if (!pmManager?.length) {
      if (!window.confirm("PM 단장을 선택하지 않았습니다. 계속 진행하시겠습니까?")) return;
    }

    try {
      setSavingNotice(true);
      // MI 문서 재조회
      const miRes = await getQualityMeta({ dns });
      const currentMeta = miRes.meta || {}; // ✅ Extract ONLY meta

      // ✅ 검수일시(inspect_date)는 PC(체크리스트)의 점검일자(check_date)를 우선 사용
      const originCheckDate = data?.meta?.header?.check_date || new Date().toISOString().split("T")[0];

      const newMeta = {
        ...currentMeta, // ✅ Spread current meta content (flattened)
        notice: {
          inspect_date: originCheckDate,
          result: notice.result,
          instruction: notice.instruction,
          notice_date: notice.notice_date,
          receiver_ids: requestPeople.map((p) => p.id),
          receiver_names: requestPeople.map((p) => p.name || p.user?.name),

          pm_manager_id: pmManager[0]?.id || null,
          pm_manager_name: pmManager[0]?.user_name || pmManager[0]?.name || null,

          // 작성 시점에는 PM 단장 서명 없음 (승인 시 추가)
          pm_manager_sign: null,

          writer_id: currentUser.id,
          writer_name: currentUser.name,
          writer_sign: currentUser.sign || null,

          ck_dns: ckDns,
        },
      };

      console.log("🚀 [DEBUG] saveNotice START");
      console.log("- Payload To Be Sent (Meta Only):", newMeta);

      const res = await updateQualityMeta(dns, newMeta); // ✅ Pass only newMeta

      console.log("✅ [DEBUG] saveNotice SUCCESS. Response:", res);

      alert("✅ 시공 품질 검수 결과 통보가 저장되었습니다.");

      // 상태 업데이트 (UI 반영)
      // 리로드 대신 상태만 리셋하거나, 재조회 로직이 필요할 수 있음.
      // 여기서는 일단 성공 알림.

    } catch (e) {
      console.error("❌ [DEBUG] saveNotice FAILED:", e);
      if (e.response) {
        console.error("- Response Data:", e.response.data);
        console.error("- Response Status:", e.response.status);
      }
      alert("통보 저장 실패 (로그 확인 필요)");
    } finally {
      setSavingNotice(false);
    }
  };

  const handlePmManagerApprove = async () => {
    if (!window.confirm("승인하시겠습니까? 서명이 등록됩니다.")) return;
    try {
      // miRes 재조회
      const miRes = await getQualityMeta({ dns });

      const currentMeta = miRes.meta || {};
      const currentNotice = currentMeta.notice || {};

      const newNotice = {
        ...currentNotice,
        pm_manager_sign: currentUser.sign, // 내 서명 입력
      };

      await updateQualityMeta(dns, {
        ...currentMeta,
        notice: newNotice,
      });

      alert("승인되었습니다.");
      // window.location.reload(); // 리로드 제거 권장 (상태 업데이트 필요)
    } catch (e) {
      console.error(e);
      alert("승인 중 오류가 발생했습니다.");
    }
  };

  if (!data) return null;

  const formatNoticeDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}.${m}.${day}`;
  };


  return (
    <div className="space-y-6">
      {/* 문서 정보 */}
      <table className="w-full border text-sm">
        <tbody>
          <tr>
            <TableCell label="수신자" value={o.receiver?.name || "-"} />
          </tr>
          <tr>
            <TableCell label="검측 요청일" value={o.request_date || "-"} />
          </tr>
          <tr>
            <TableCell
              label="위치 및 공종"
              colSpan={3}
              value={o.location_work || "-"}
            />
          </tr>
          <tr>
            <TableCell
              label="검측 부위"
              colSpan={3}
              value={o.inspection_part || "-"}
            />
          </tr>
          <tr>
            <TableCell
              label="검측 사항"
              colSpan={3}
              value={o.inspection_item || "-"}
            />
          </tr>
        </tbody>
      </table>

      {/* 승인 / 사인 / 다음 단계 */}
      {dns && (
        <div className="flex justify-end gap-4 items-start flex-wrap">
          {/* 승인 버튼 */}
          {isSiteAgent && !approved && (
            <button
              className="btn-basic-sm"
              disabled={saving}
              onClick={handleApprove}
            >
              {saving ? "저장 중..." : "승인"}
            </button>
          )}

          {/* 체크리스트 작성 */}
          {isSiteAgent && approved && (
            <button className="btn-basic-sm" onClick={goCreateChecklist}>
              검측요청서 체크리스트 작성하기
            </button>
          )}

          {/* 사인 테이블 (조회용) */}
          <SignTableDocByRole
            dns={dns}
            project={project}
            officialMeta={o}
            showAgentSign={approved}
          />
        </div>
      )}
      {/* ...기존 내용... */}

      {/* 서명 테이블 */}
      {/* ...원래 있던 서명 테이블이 있다면 유지... */}

      {/* ===============================
          시공 품질 검수 결과 통보서
      =============================== */}
      {ckApproved && (
        <div className="mt-8 border-t pt-8 print:border-none">
          <div className="text-xl font-bold mb-4">
            검측 결과 통보서 <span className="text-base font-normal text-gray-500 ml-2">(Inspection Result Report)</span>
          </div>
          {!savedNoticeExists ? (
            <>
              {/* 상단 정보 테이블 */}
              <table className="doc-table w-full text-xs mb-4">
                <tbody>
                  <tr>
                    <td className="tbl-label-bg w-[150px]">문서 번호</td>
                    <td className="tbl-value-bg">
                      {ckDns || savedNotice?.ck_dns || "-"}
                    </td>

                    <td className="tbl-label-bg w-[150px]">통보 일자</td>
                    <td className="tbl-value-bg">
                      <input
                        type="date"
                        className="input-base w-full text-xs"
                        value={notice.notice_date}
                        onChange={(e) =>
                          setNotice((p) => ({ ...p, notice_date: e.target.value }))
                        }
                      />
                    </td>
                  </tr>

                  <tr>
                    <td className="tbl-label-bg">수 신</td>
                    <td className="tbl-value-bg" colSpan={3}>
                      <PeopleCellLoader
                        single
                        dns={dns}
                        value={requestPeople}
                        onChange={setRequestPeople}
                        placeholder="현장대리인을 선택하세요"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* ✅ 본문 문장 (보기모드와 동일 모양) */}
              <div className="mb-4 text-body">
                문서번호 <b>{dns}</b> 로 요청한 건에 대하여{" "}
                <b>
                  {notice.notice_date ? formatNoticeDate(notice.notice_date) : "____.__.__"}
                </b>{" "}
                검측한 결과를 다음과 같이 통보합니다.
              </div>

              {/* ✅ 본문 테이블 (보기모드와 동일 모양) */}
              <table className="w-full border text-sm">
                <tbody>
                  <tr>
                    <td className="tbl-label-bg w-[150px]">1.검측결과</td>
                    <td className="tbl-value-bg">
                      <textarea
                        className="input-base w-full text-xs"
                        rows={3}
                        value={notice.result || ""}
                        onChange={(e) =>
                          setNotice((p) => ({ ...p, result: e.target.value }))
                        }
                        placeholder="결과를 입력하세요."
                      />
                    </td>
                  </tr>

                  <tr>
                    <td className="tbl-label-bg w-[150px]">2.지시사항</td>
                    <td className="tbl-value-bg">
                      <textarea
                        className="input-base w-full text-xs"
                        rows={3}
                        value={notice.instruction || ""}
                        onChange={(e) =>
                          setNotice((p) => ({ ...p, instruction: e.target.value }))
                        }
                        placeholder="지시사항을 입력하세요."
                      />
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* 서명 영역 (기존 유지) */}
              <div className="flex justify-end gap-16 pr-4 mb-4 text-xs mt-8">
                <div className="text-center">
                  <div className="mb-8 font-bold">담당 PM</div>
                  <div>
                    {currentUser.sign ? (
                      <img src={currentUser.sign} alt="sign" className="h-10 mx-auto" />
                    ) : (
                      <span>{currentUser.name} (인)</span>
                    )}
                  </div>
                </div>

                <div className="text-center">
                  <div className="mb-8 font-bold">PM 단장</div>
                  <div className="min-w-[100px]">
                    <PeopleCellLoader
                      single
                      dns={dns}
                      value={pmManager}
                      onChange={setPmManager}
                      placeholder="담당자 선택"
                    />
                  </div>
                </div>
              </div>

              {/* 저장 버튼 */}
              <div className="flex justify-end mt-4">
                <button
                  className="btn-basic-sm"
                  disabled={savingNotice}
                  onClick={saveNotice}
                >
                  {savingNotice ? "저장 중..." : "저장하고 통보하기"}
                </button>
              </div>
            </>
          ) : (
            /* 보기 모드 */
            <>

              <table className="doc-table w-full text-xs mb-4">
                <tbody>
                  <tr>
                    <td className="tbl-label-bg w-[150px]">문서 번호</td>
                    <td className="tbl-value-bg">
                      {savedNotice.ck_dns || ckDns || "-"}
                    </td>
                    <td className="tbl-label-bg w-[150px]">통보 일자</td>
                    <td className="tbl-value-bg">
                      {savedNotice.notice_date || "-"}
                    </td>
                  </tr>
                  <tr>
                    <td className="tbl-label-bg">수 신</td>
                    <td className="tbl-value-bg" colSpan={3}>
                      {(savedNotice.receiver_names || []).join(", ") || "-"}
                    </td>
                  </tr>
                  {/* <tr>
                    <td className="tbl-value-bg p-4" colSpan={4}>
                      <div className="mb-4 text-gray-700">
                        문서번호 <b>{dns}</b> 로 요청한 건에 대하여 검측한 결과를 다음과 같이 통보합니다.
                      </div>

                      <div className="mb-2 font-bold">1. 검 측 결 과</div>
                      <div className="whitespace-pre-wrap min-h-[60px] mb-4 text-gray-800 bg-white p-2 rounded border border-gray-100">
                        {savedNotice.result || "-"}
                      </div>

                      <div className="mb-2 font-bold">2. 지 시 사 항</div>
                      <div className="whitespace-pre-wrap min-h-[60px] text-gray-800 bg-white p-2 rounded border border-gray-100">
                        {savedNotice.instruction || "-"}
                      </div>
                    </td>
                  </tr> */}
                </tbody>
              </table>
              <div className="mb-4 text-body">
                문서번호 <b>{dns}</b> 로 요청한 건에 대하여{" "}
                <b>
                  {savedNotice.notice_date
                    ? formatNoticeDate(savedNotice.notice_date)
                    : "____.__.__"}
                </b>{" "}
                검측한 결과를 다음과 같이 통보합니다.
              </div>

              <table className="w-full border text-sm">
                <tbody>
                  <tr>
                    <TableCell label="1.검측결과" value={savedNotice.result || "-"} />
                  </tr>
                  <tr>
                    <TableCell label="2.지시사항" value={savedNotice.instruction || "-"} />
                  </tr>
                </tbody>
              </table>

              <div className="flex justify-end gap-16 pr-4 mb-8 text-xs mt-8">
                <div className="text-center">
                  <div className="mb-8 font-bold">담당 PM</div>
                  <div>
                    {savedNotice.writer_sign ? (
                      <img src={savedNotice.writer_sign} alt="sign" className="h-10 mx-auto" />
                    ) : (
                      <span>{savedNotice.writer_name} (인)</span>
                    )}
                  </div>
                </div>
                <div className="text-center">
                  <div className="mb-8 font-bold">PM 단장</div>
                  <div>
                    {savedNotice.pm_manager_sign ? (
                      <img src={savedNotice.pm_manager_sign} alt="sign" className="h-10 mx-auto" />
                    ) : (
                      // 서명이 없고 + 내가 PM 단장 본인이면 -> 승인 버튼
                      (Number(savedNotice.pm_manager_id) === Number(currentUser.id)) ? (
                        <button
                          onClick={handlePmManagerApprove}
                          className="btn-save-sm"
                        >
                          승인하기
                        </button>
                      ) : (
                        <span className="text-gray-400">
                          {savedNotice.pm_manager_name ? `${savedNotice.pm_manager_name} (미승인)` : "(인)"}
                        </span>
                      )
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

        </div>
      )}
    </div>
  );
}
